/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment-timezone';
import Header from './Header';
import ButtonGroup from '../controls/ButtonGroup';
import {
  Alert,
  DashboardDataFilter,
  EventAttribute,
  ScrollContainerAttribute
} from '../constants/telosair-interfaces';
import { useData } from '../context/data.context';
import { getBgClassNameByAlert, getParameterByName } from '../_helpers/globalHelper';
import Button from '../controls/Button';
import AddNotesModal from '../modals/AddNotes';
import ScrollView from './ScrollContainer';
import Loader from './loader';
import AddEventsModal from '../modals/AddEvents';
import TruncatedText from './truncatedText';

type CommentsCompProps = {
  events: EventAttribute[];
  dataFilter: DashboardDataFilter | null;
};

/**
 * Notes page renders list of Notes.
 * @param props - Properties of thr component.
 * @returns - ReactElement.
 */
function Comments(props: CommentsCompProps): ReactElement {
  const { events, dataFilter } = props;
  const { userConfigData, groupedData } = useData();
  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [filteredEvents, setFilteredEvents] = useState<EventAttribute[]>([]);

  useEffect(() => {
    if (dataFilter?.type === 'group') {
      const filteredGroup: any = groupedData.filter((item) => item.groupId === dataFilter.id)[0];
      if (filteredGroup) {
        const devicesInGroup = filteredGroup.sensorDetailsList?.map(
          (item: any) => item.serial_number
        );
        const filteredEventsList = events.filter((event) => {
          if (!event.devices) return false;
          return event.devices.some((deviceId) => devicesInGroup?.includes(Number(deviceId)));
        });
        setFilteredEvents(filteredEventsList);
      }
    } else if (dataFilter?.type === 'device') {
      setFilteredEvents(events.filter((item) => item.devices?.includes(String(dataFilter.id))));
    } else {
      setFilteredEvents(events);
    }
  }, [events, dataFilter, groupedData]);

  if (!filteredEvents || filteredEvents.length === 0)
    return <div className="text-center text-gray-500 text-xs my-3">Nothing added yet!</div>;

  return (
    <div>
      {filteredEvents.map((item) => (
        <div className="flex w-full items-start justify-between mt-4 " key={item.id}>
          <div className="max-w-[65%]">
            <div className="items-start gap-3 flex text-gray-300 text-sm break-words">
              <div>&bull; </div>
              <div>
                <div className="text-left break-words max-w-[70%]">
                  <TruncatedText text={`${item.eventNotes}`} />
                </div>
                <div className="text-xs text-left font-medium text-gray-400">
                  Event time: {moment(item.eventTime).tz(timeZone).format('Do MMM hh:mm a')}
                </div>
              </div>
            </div>
            {/* <div className="items-start flex gap-3 text-gray-400 text-xs font-normal w-fit mt-2">
              <div className="invisible">&bull; </div>
              <div className="text-left">{item.createdByUser}</div>
            </div> */}
          </div>
          <div className="text-xs font-medium text-gray-400">
            {moment(item.createdDate).tz(timeZone).format('Do MMM hh:mm a')}
          </div>
        </div>
      ))}
    </div>
  );
}

type AlertCompProps = {
  alerts: Alert[];
  dataFilter: DashboardDataFilter | null;
};
/**
 * Alerts page renders list of Alerts.
 * @param props - Properties of th component.
 * @returns - ReactElement.
 */
function Alerts(props: AlertCompProps): ReactElement {
  const { alerts, dataFilter } = props;

  const { alertRules, parameters, userConfigData, groupedData } = useData();

  const [filteredAlerts, setFilteredAlerts] = useState<Alert[]>([]);

  useEffect(() => {
    if (dataFilter?.type === 'group') {
      const filteredGroup: any = groupedData.filter((item) => item.groupId === dataFilter.id)[0];
      if (filteredGroup) {
        const devicesInGroup = filteredGroup.sensorDetailsList?.map(
          (item: any) => item.serial_number
        );
        setFilteredAlerts(alerts.filter((item) => devicesInGroup.includes(item.serialNumber)));
      }
    } else if (dataFilter?.type === 'device') {
      setFilteredAlerts(alerts.filter((item) => item.serialNumber.includes(String(dataFilter.id))));
    } else {
      setFilteredAlerts(alerts);
    }
  }, [groupedData, dataFilter, alerts]);

  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (filteredAlerts.length === 0) {
    return <div className="text-center text-gray-500 text-xs my-3">0 Alerts</div>;
  }
  return (
    <div>
      {filteredAlerts.map((alert) => {
        const rule = alertRules.filter((item) => item.id === alert.alertId);
        const parameter = getParameterByName(alert.parameter, parameters);
        return (
          <Link
            to={`/alerts?id=${alert.id}`}
            key={alert.id}
            className="flex w-full items-center justify-between mt-3 "
          >
            <div className="items-center gap-3 flex max-w-[80%] min-h-[4rem]">
              <div
                className={`w-11 min-w-[2.75rem] h-11 ${getBgClassNameByAlert(
                  rule
                )} rounded-full flex items-center justify-center font-bold text-xs text-gray-200 overflow-hidden`}
              >
                {parameter?.shortName || '-'}
              </div>
              <div>
                <div className="text-gray-300 text-sm font-medium text-left break-words">
                  {alert.name}
                </div>
                <div className="justify-start text-gray-500 text-xs font-normal w-fit mt-1">
                  #{alert.serialNumber}{' '}
                  {parameter ? ` | ${parameter.displayName}: ${alert.avgValue}` : '-'}
                </div>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-400">
              {moment(alert.timestamp).tz(timeZone).fromNow()}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * SingleActivity component renders activity.
 * @param props - Properties of single activity component.
 * @returns - ReactElement.
 */
function SingleActivity(props: any): ReactElement {
  const { currentActivity } = props;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={clsx({ 'dark:bg-gray-900': isOpen })}>
      <div className="flex p-4">
        <div className="rounded-full h-8 w-8 border border-gray-400 dark:border-background-400" />
        <div className=" text-gray-400 dark:text-gray-300 text-sm font-medium ml-4 text-left">
          {currentActivity.actIvityName}
          <p className="text-gray-400 dark:text-gray-400 font-normal text-xs text-start">
            {currentActivity.timeOfActivity}
          </p>
        </div>

        <button
          type="button"
          className="material-icons-outlined text-md cursor-pointer text-gray-500 font-medium ml-auto"
          onClick={() => setIsOpen((prevState) => !prevState)}
        >
          {isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        </button>
      </div>
      {isOpen && (
        <div className="text-gray-400 dark:text-gray-300 text-sm font-medium ml-16 py-4 text-left">
          &quot;{currentActivity.description}&quot;
        </div>
      )}
    </div>
  );
}

const ACTIVITIES: any = [
  // {
  //   id: 1,
  //   actIvityName: 'Devices #4242 moved to “Kitchen”',
  //   timeOfActivity: '03:50 PM',
  //   description: 'Identified issue, will be solved within a hour'
  // },
  // {
  //   id: 2,
  //   actIvityName: 'Devices #4242 moved to “Kitchen”',
  //   timeOfActivity: '03:50 PM',
  //   description: 'Identified issue 2, will be solved within a hour'
  // },
  // {
  //   id: 3,
  //   actIvityName: 'Devices #4242 moved to “Kitchen”',
  //   timeOfActivity: '03:50 PM',
  //   description: 'Identified issue 3, will be solved within a hour'
  // },
  // {
  //   id: 4,
  //   actIvityName: 'Devices #4242 moved to “Kitchen”',
  //   timeOfActivity: '03:50 PM',
  //   description: 'Identified issue 4, will be solved within a hour'
  // }
];

/**
 * A component which renders list of Activity.
 * @returns - Activity Component.
 */
function RenderActivity(): ReactElement {
  return (
    <>
      {ACTIVITIES.length === 0 && (
        <div className="text-center text-gray-500 text-xs my-3">0 Activity</div>
      )}
      {ACTIVITIES.map((currentActivity: any) => {
        return <SingleActivity key={currentActivity.id} currentActivity={currentActivity} />;
      })}
    </>
  );
}

const TABS = [
  // { id: 'all-activity', name: 'All Activity' },
  { id: 'alerts', name: 'Alerts' },
  { id: 'events', name: 'Events' }
];

/**
 * A DevicesActivity Component renders All the different Tabs.
 * @param props - Properties of the component.
 * @param props.dataFilter - Data filter.
 * @param props.extendedLabel - Extended label.
 * @returns - DevicesActivity Component.
 */
export default function ActivitiesWidget({
  dataFilter,
  extendedLabel
}: {
  dataFilter: DashboardDataFilter | null;
  extendedLabel: string;
}): ReactElement {
  const { allAlerts, loading, events } = useData();
  const [activeTab, setActiveTab] = useState('alerts');
  const [scrollContainerData, setScrollContainerData] = useState<ScrollContainerAttribute>({
    loaded: false,
    height: null,
    width: null
  });
  const [showAddNotesModal, setShowAddNotesModal] = useState(false);
  const topDeviceAlerts = allAlerts.filter((item) => item.status === 'pending');

  return (
    <>
      <div className="rounded bg-white dark:bg-container-500 shadow-md text-center w-full pt-4 pb-3 flex h-full flex-col">
        <div className="px-4">
          <Header sectionName="All Activity">
            <span className="text-sm font-regular text-gray-400  cursor-default">
              {extendedLabel}
            </span>
          </Header>
        </div>

        <div className="mt-4 px-4">
          <ButtonGroup
            buttons={TABS}
            size="sm"
            handleClick={setActiveTab}
            activeButton={activeTab}
          />
        </div>
        <ScrollView
          scrollContainerData={scrollContainerData}
          setScrollContainerData={setScrollContainerData}
          loading={loading}
        >
          {!scrollContainerData.loaded && <Loader />}
          {scrollContainerData.loaded && (
            <div
              style={{
                height: scrollContainerData.height || 'auto'
              }}
              className="text-gray-400 text-xs font-normal relative overflow-y-auto border-b border-b-gray-600 mx-4"
            >
              {activeTab === 'events' && <Comments dataFilter={dataFilter} events={events} />}
              {activeTab === 'alerts' && (
                <Alerts alerts={topDeviceAlerts} dataFilter={dataFilter} />
              )}
            </div>
          )}
        </ScrollView>

        <div className="flex justify-center items-center pt-3 h-12">
          {activeTab === 'events' && (
            <div>
              <Button
                onClick={() => setShowAddNotesModal(true)}
                theme="secondary-color"
                size="sm"
                name="Add Event"
                isBlockButton
              />
            </div>
          )}
        </div>
      </div>
      {showAddNotesModal && <AddEventsModal handleClose={() => setShowAddNotesModal(false)} />}
    </>
  );
}
